import {
  LatLon,
  Road,
  RoadClass,
  POI,
  POIType,
  NeighborhoodDNA,
} from '../domain/types.ts';
import { OverpassElement } from './osmClient.ts';
import { Projection } from './projection.ts';

export class Normalizer {
  public static normalizeRoads(elements: OverpassElement[], center: LatLon): Road[] {
    const roads: Road[] = [];

    for (const el of elements) {
      if (el.type !== 'way' || !el.tags || !el.tags.highway || !el.geometry) {
        continue;
      }

      const roadClass = this.mapRoadClass(el.tags.highway);
      const points = el.geometry.map((g) => Projection.project({ lat: g.lat, lon: g.lon }, center));

      if (points.length >= 2) {
        roads.push({
          id: `road-${el.id}`,
          name: el.tags.name,
          roadClass,
          points,
        });
      }
    }

    return roads;
  }

  public static normalizePOIs(elements: OverpassElement[], center: LatLon): POI[] {
    const pois: POI[] = [];

    for (const el of elements) {
      if (el.type !== 'node' || el.lat === undefined || el.lon === undefined || !el.tags) {
        continue;
      }

      const poiType = this.mapPOIType(el.tags);
      if (!poiType) continue;

      const pos = Projection.project({ lat: el.lat, lon: el.lon }, center);
      pois.push({
        id: `poi-${el.id}`,
        name: el.tags.name,
        type: poiType,
        position: pos,
      });
    }

    return pois;
  }

  public static calculateDNA(
    elements: OverpassElement[],
    roads: Road[],
    pois: POI[]
  ): NeighborhoodDNA {
    let buildingCount = 0;
    for (const el of elements) {
      if (el.tags && el.tags.building) {
        buildingCount++;
      }
    }

    // Heuristics chuẩn hóa [0, 1]
    const density = Math.min(1.0, buildingCount / 80);
    const roadTraffic = Math.min(1.0, roads.length / 25);
    const schoolCount = pois.filter((p) => p.type === 'school').length;
    const hospitalCount = pois.filter((p) => p.type === 'hospital').length;
    const marketCount = pois.filter((p) => p.type === 'market').length;
    const realStallCount = pois.filter((p) => p.type === 'real_stall').length;

    return {
      density: Math.max(0.2, Math.min(1.0, (buildingCount + realStallCount * 4) / 80)),
      roadTraffic: Math.max(0.3, roadTraffic),
      pedestrianFlow: Math.min(1.0, 0.3 + density * 0.4 + roadTraffic * 0.3 + Math.min(0.25, realStallCount * 0.04)),
      schoolFlow: Math.min(1.0, schoolCount * 0.3),
      hospitalFlow: Math.min(1.0, hospitalCount * 0.4),
      marketFlow: Math.min(1.0, marketCount * 0.5),
      nightFlow: Math.min(1.0, 0.2 + density * 0.5),
      competition: Math.min(1.0, 0.3 + density * 0.3 + Math.min(0.3, realStallCount * 0.05)),
      risk: Math.min(0.8, 0.2 + roadTraffic * 0.4),
    };
  }

  private static mapRoadClass(highwayTag: string): RoadClass {
    switch (highwayTag) {
      case 'primary':
      case 'primary_link':
        return 'primary';
      case 'secondary':
      case 'secondary_link':
        return 'secondary';
      case 'tertiary':
      case 'tertiary_link':
        return 'tertiary';
      case 'residential':
        return 'residential';
      case 'living_street':
        return 'living_street';
      case 'footway':
      case 'pedestrian':
        return 'footway';
      default:
        return 'residential';
    }
  }

  private static mapPOIType(tags: Record<string, string>): POIType | null {
    if (tags.amenity === 'school') return 'school';
    if (tags.amenity === 'hospital') return 'hospital';
    if (tags.amenity === 'marketplace') return 'market';
    if (tags.highway === 'bus_stop' || tags.amenity === 'bus_station') return 'bus_stop';
    if (
      tags.amenity === 'cafe' ||
      tags.amenity === 'restaurant' ||
      tags.amenity === 'fast_food' ||
      tags.shop === 'convenience' ||
      tags.shop === 'kiosk'
    ) {
      return 'real_stall';
    }
    return null;
  }
}
